import { FC, useEffect, useRef, useState } from "react";
import { ColumnConfig, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { UserSessionService } from "@app/admin/user-sessions/userSessionService";
import { UserSession } from "@app/admin/user-sessions/apiModels";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { breadcrumbTemplates, BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { UserInfo } from "@app/user/UserContext";

interface UserSessionsProps {
  user: UserInfo;
}

interface UserSessionRecord {
  clientName: string;
  key: string;
  name: string;
  sessionStartDate: string | Date;
  userEmail: string;
  username: string;
}

const columnConfig: ColumnConfig = {
  clientName: {
    displayText: "Client",
    testId: "client-name",
  },
  name: {
    displayText: "Name",
    testId: "name",
  },
  sessionStartDate: {
    displayText: "Session Start Date",
    testId: "session-start-date",
  },
  userEmail: {
    displayText: "Email",
    testId: "user-email",
  },
  username: {
    displayText: "Username",
    testId: "username",
  },
};

export const UserSessions: FC<UserSessionsProps> = (props: UserSessionsProps) => {
  const userSessionService = useRef(new UserSessionService()).current;
  const [loaded, setLoaded] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState<UserSession[]>([]);
  const [userSessions, setUserSessions] = useState<UserSession[]>([]);
  const breadcrumbs = breadcrumbTemplates[BreadcrumbTemplateEnum.UserSessions];

  useEffect(() => {
    // filter out the current user session, we may have to undo this later
    // if so, we'll need be sure to either stop the user from potentially terminating
    // their own current session...or notify them that they've selected their own
    // session for termination
    userSessionService.get().then((userSessions) => {
      const newUserSessions = userSessions.filter((session) => session.userId !== props.user.userId);
      setLoaded(true);
      setUserSessions(newUserSessions);
    })
  }, []);

  const renderRecords = () => {
    /* oxlint-disable sort-keys */
    const records: UserSessionRecord[] = userSessions.map((s) => {
      return {
        [wafflesKeyColumnName]: s.sessionId,
        name: s.userDisplayName,
        username: s.username,
        userEmail: s.userEmail,
        clientName: s.clientName,
        sessionStartDate: formatLocalDate(s.sessionStartDate),
      };
    });
    /* oxlint-enable sort-keys */
    const selectedKeys = selectedSessions.map((session) => session.sessionId);
    const showBulkAction = selectedKeys.length > 0;

    const bulkActionBar = (
      <span>
        {showBulkAction
          ? (
            <a href="#" onClick={handleEndSessionClick}><QesIcon iconName={QesIcons.SlashOctagon} description="End Session" className="mb4" /> End Session</a>
        ) : (<span>&nbsp;</span>)}
      </span>
    );

    return (
      <>
        <h5 className="mb-0">Active Sessions</h5>
        <Waffles<UserSessionRecord>
          bulkActionBar={bulkActionBar}
          columns={columnConfig}
          data={records}
          fullWidth={true}
          handleCheckboxChange={handleCheckboxChange}
          selectedKeys={selectedKeys}
          showCheckboxes={true}
          testId="user-session-table"
        />
      </>
    );
  }

  const formatLocalDate = (dateToFormat: Date) => {
    const formattedDate = new Date(dateToFormat.toString());
    return `${formattedDate.toLocaleDateString()} ${formattedDate.toLocaleTimeString()}`;
  }

  const handleCheckboxChange = (selectedKeys: string[]) => {
    const newSelectedSessions = userSessions.filter((session) => selectedKeys.includes(session.sessionId));
    setSelectedSessions(newSelectedSessions);
  }

  const handleEndSessionClick = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (selectedSessions.length > 0) {
      await userSessionService.patch(selectedSessions);
      const newUserSessions = userSessions.filter((session) => !selectedSessions.includes(session));

      setSelectedSessions([]);
      setUserSessions(newUserSessions);
    }
  }

  return (
    <Page documentTitle="User Sessions - Admin">
      <Breadcrumbs
        items={breadcrumbs}
      />
      <PageInner>
        <PageHeader title="User Sessions" />
        <PageSection>
          <div className="row mt-3">
            <div className="col-sm-12">
              {!loaded ? (<LoadingSpinner />) : renderRecords()}
            </div>
          </div>
        </PageSection>
      </PageInner>
    </Page>
  );
}
